// Notification Service
// Handles push notifications for streak warnings, reminders, and achievements

import { logger } from '../utils/logger';
import { gamificationService } from './gamificationService';

export interface NotificationOptions {
  title: string;
  body: string;
  icon?: string;
  badge?: string;
  tag?: string;
  requireInteraction?: boolean;
  silent?: boolean;
  timestamp?: number;
  data?: any;
}

class NotificationService {
  private permission: NotificationPermission = 'default';
  private isSupported: boolean = false;

  constructor() {
    this.checkSupport();
    this.requestPermission();
  }

  private checkSupport(): void {
    this.isSupported = 'Notification' in window && 'serviceWorker' in navigator;
    if (!this.isSupported) {
      logger.warn('Notifications not supported in this browser');
    }
  }

  async requestPermission(): Promise<NotificationPermission> {
    if (!this.isSupported) {
      return 'denied';
    }

    try {
      this.permission = await Notification.requestPermission();
      logger.info('Notification permission:', this.permission);
      return this.permission;
    } catch (error) {
      logger.error('Error requesting notification permission:', error);
      return 'denied';
    }
  }

  canNotify(): boolean {
    return this.isSupported && this.permission === 'granted';
  }

  // Schedule morning reminder (8 AM)
  scheduleMorningReminder(streakDays: number): void {
    if (!this.canNotify()) return;

    if (streakDays >= 3) {
      this.scheduleNotification({
        time: '08:00',
        title: `🔥 Don't break your ${streakDays}-day streak!`,
        body: 'Complete a practice test today to keep it going!',
        icon: '🔥',
        tag: 'morning-reminder',
        requireInteraction: false
      });
    }
  }

  // Schedule evening reminder (8 PM) - Last chance
  scheduleEveningReminder(streakDays: number): void {
    if (!this.canNotify()) return;

    if (streakDays >= 7) {
      this.scheduleNotification({
        time: '20:00',
        title: `⚠️ Your ${streakDays}-day streak is at risk!`,
        body: 'You have 4 hours left to keep your streak alive!',
        icon: '⚠️',
        tag: 'evening-reminder',
        requireInteraction: true
      });
    }
  }

  // Streak loss warning (11 PM)
  scheduleStreakLossWarning(streakDays: number): void {
    if (!this.canNotify()) return;

    if (streakDays >= 5) {
      this.scheduleNotification({
        time: '23:00',
        title: `🚨 Last chance! Your ${streakDays}-day streak ends in 1 hour!`,
        body: 'Quick! Complete just 1 practice test to save it!',
        icon: '🚨',
        tag: 'streak-warning',
        requireInteraction: true
      });
    }
  }

  // Achievement notification
  scheduleAchievementNotification(achievement: { title: string; description: string }): void {
    if (!this.canNotify()) return;

    this.showNotification({
      title: `🎉 Achievement Unlocked!`,
      body: `${achievement.title} - ${achievement.description}`,
      icon: '🎉',
      tag: 'achievement',
      requireInteraction: false
    });
  }

  // Level up notification
  scheduleLevelUpNotification(newLevel: number): void {
    if (!this.canNotify()) return;

    this.showNotification({
      title: `⭐ Level ${newLevel} Unlocked!`,
      body: 'You\'re getting better! Keep it up!',
      icon: '⭐',
      tag: 'level-up',
      requireInteraction: false
    });
  }

  // Show immediate notification
  showNotification(options: NotificationOptions): void {
    if (!this.canNotify()) {
      logger.warn('Cannot show notification: permission denied or not supported');
      return;
    }

    try {
      const notification = new Notification(options.title, {
        body: options.body,
        icon: options.icon || '/icon-192x192.png',
        badge: options.badge || '/icon-192x192.png',
        tag: options.tag || 'default',
        requireInteraction: options.requireInteraction || false,
        silent: options.silent || false,
        timestamp: options.timestamp || Date.now(),
        data: options.data || {}
      });

      notification.onclick = () => {
        window.focus();
        notification.close();
      };

      // Auto-close after 5 seconds
      setTimeout(() => {
        notification.close();
      }, 5000);
    } catch (error) {
      logger.error('Error showing notification:', error);
    }
  }

  // Schedule notification for specific time
  private scheduleNotification(options: { time: string; title: string; body: string; icon?: string; tag?: string; requireInteraction?: boolean }): void {
    // For now, we'll use immediate notifications
    // In production, you'd use a service worker and background sync
    // This is a simplified version for web app
    
    const [hours, minutes] = options.time.split(':').map(Number);
    const now = new Date();
    const scheduledTime = new Date();
    scheduledTime.setHours(hours, minutes, 0, 0);

    // If time has passed today, schedule for tomorrow
    if (scheduledTime < now) {
      scheduledTime.setDate(scheduledTime.getDate() + 1);
    }

    const delay = scheduledTime.getTime() - now.getTime();

    setTimeout(() => {
      this.showNotification({
        title: options.title,
        body: options.body,
        icon: options.icon,
        tag: options.tag,
        requireInteraction: options.requireInteraction
      });
    }, delay);
  }

  // Schedule break reminder after extended study session
  scheduleBreakReminder(_sessionStartTime: number): void {
    if (!this.canNotify()) return;
    // Remind after 30 minutes
    setTimeout(() => {
      this.showNotification({
        title: 'Time for a break!',
        body: "You've been studying for 30 minutes. Take a short break to stay focused!",
        tag: 'break-reminder',
        requireInteraction: false,
      });
    }, 30 * 60 * 1000);
  }

  showAchievementNotification(achievement: { title: string; description: string }): void {
    this.scheduleAchievementNotification(achievement);
  }

  showDailyGoalCompletionNotification(_target: number): void {
    if (!this.canNotify()) return;
    this.showNotification({
      title: 'Daily Goal Complete!',
      body: 'Great job completing your daily practice goal!',
      tag: 'daily-goal',
      requireInteraction: false,
    });
  }

  initializeDailyNotifications(): void {
    const data = gamificationService.getData();
    const streakDays = data.dailyStudyStreak || 0;

    // Schedule all daily notifications
    this.scheduleMorningReminder(streakDays);
    this.scheduleEveningReminder(streakDays);
    this.scheduleStreakLossWarning(streakDays);
  }
}

export const notificationService = new NotificationService();

